/**
 * Captura do código de indicação vindo de link (`?ref=CODE`).
 *
 * O clique no link do creator quase nunca acontece logado: o visitante cai
 * na landing, cria conta, passa pelo onboarding e só então existe um `uid`
 * pra atribuir. Por isso o código é guardado em `localStorage` e só aplicado
 * quando há sessão — sobrevive ao signup, ao redirect do login Google e a
 * um reload no meio do caminho.
 *
 * A garantia de "primeira atribuição vence" é do SERVIDOR
 * (`redeemCreatorCode` nunca sobrescreve `referredBy`). Aqui a última
 * captura vence de propósito: se a pessoa clicou em dois links de creators
 * diferentes antes de criar conta, o mais recente é o que ela viu por
 * último — e o servidor ainda assim não troca uma atribuição já existente.
 */

const STORAGE_KEY = "livra:pending_ref";
const REF_PARAM = "ref";

/** Mesmo charset de `RedeemCreatorCodeSchema` no servidor. */
const CODE_RE = /^[A-Za-z0-9_-]{3,40}$/;

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Safari em modo privado / storage bloqueado. Referral degrada pra
    // "não atribuído", nunca quebra o app.
    return null;
  }
}

export function normalizeReferralCode(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? "").trim();
  if (!CODE_RE.test(trimmed)) return null;
  return trimmed.toUpperCase();
}

/**
 * Lê `?ref=` da URL, guarda e limpa o parâmetro da barra de endereço (pra
 * não vazar em screenshot nem em link recompartilhado). Devolve o código
 * capturado, se houver.
 */
export function captureReferralFromUrl(search?: string): string | null {
  const query = search ?? window.location.search;
  const code = normalizeReferralCode(new URLSearchParams(query).get(REF_PARAM));
  if (!code) return null;

  const storage = safeLocalStorage();
  storage?.setItem(STORAGE_KEY, code);

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(REF_PARAM)) {
      url.searchParams.delete(REF_PARAM);
      // ⚠️ Reemite o state ATUAL, nunca `{}`. O React Router guarda em
      // `history.state.idx` a contagem de entradas do app, e é ela que o
      // `hasInAppHistory()` (routes/useBack.ts) lê para decidir entre
      // `navigate(-1)` e subir para o pai declarado. Um `{}` aqui apaga o
      // `idx` da entrada, e quem entrou por `?ref=` passa a ter todo botão
      // de voltar do app tratado como deep link até a próxima navegação.
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    // replaceState indisponível (SSR/webview antigo) — irrelevante, o
    // código já foi guardado.
  }

  return code;
}

export function readPendingReferral(): string | null {
  return normalizeReferralCode(safeLocalStorage()?.getItem(STORAGE_KEY));
}

export function clearPendingReferral(): void {
  safeLocalStorage()?.removeItem(STORAGE_KEY);
}
